import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { PAYIN__SH_INVOICE_INCLUDES } from "../../../../../../constants/routes/admin/accounting/payin";
import { showModal } from "sh-www/utils/modal-controls";
import { task } from 'ember-concurrency';

export default Service.extend({
  /**
   * Register Services
   */
  applicationStateManager: service('state-managers/application'),
  session: service(),
  shInvoiceCommandService : service('commands/sh-invoice-commands'),
  shInvoiceLineItemsCommandService : service('commands/sh-invoice-line-item-commands'),
  shInvoiceTransactionCommandService: service('commands/sh-invoice-transaction-commands'),
  state: service('states/routes/admin/accounting/payin/sh-invoice'),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * Add the comma delimited text of invoice ids to the batched invoice.
   *
   * @param {String} invoiceIdsDelimitedText    Comma delimited list of invoice ids
   * @return {Ember.RSVP.Promise}
   */
  addInvoicesToBatch(invoiceIdsDelimitedText) {
    this.set('state.isLoadingInvoice', true);

    let invoiceIds = invoiceIdsDelimitedText.split(',');
    return this.get('shInvoiceCommandService').addInvoicesToBatch(
      this.get('state.shInvoice'),
      invoiceIds
    ).then(() => {
      this.set('state.isLoadingInvoice', false);
      this.loadShInvoice(this.get('state.shInvoice.id'));
    }).catch(() => {
      this.set('state.isLoadingInvoice', false);
    });
  },

  /**
   * Send an invoice to the user of the invoice.
   *
   * @return {Ember.RSVP.Promise}
   */
  approve() {
    this.set('state.isApproving', true);

    return this.get('shInvoiceCommandService').approveInvoice(
      this.get('state.shInvoice'),
      PAYIN__SH_INVOICE_INCLUDES
    ).then(() => {
      this.get('applicationStateManager').addToast('Invoice approved');
    }).catch((e) => {
      handleErrorResponseWithModal(
        this,
        e,
        'Error approving the invoice.  Please refresh and try again.',
        'approveInvoice -- error approving invoice id : ' + this.get('state.shInvoice.id'),
      );
    }).finally(() => {
      this.set('state.isApproving', false);
    });
  },

  /**
   * Send an invoice to the user of the invoice.
   *
   * @return {Ember.RSVP.Promise}
   */
  declineExpedite() {
    this.set('state.isApproving', true);

    return this.get('shInvoiceCommandService').declineExpediteInvoice(
      this.get('state.shInvoice'),
      PAYIN__SH_INVOICE_INCLUDES
    ).then(() => {
      this.get('applicationStateManager').addToast('Invoice expedite removed, invoice approved');
    }).catch((e) => {
      handleErrorResponseWithModal(
        this,
        e,
        'Error approving the invoice.  Please refresh and try again.',
        'approveInvoice -- error approving invoice id : ' + this.get('shInvoice.id'),
      );
    }).finally(() => {
      this.set('state.isApproving', false);
    });
  },

  /**
   * Delete the line item and update our state.
   *
   * @param {ShInvoiceLineItem} shInvoiceLineItem
   */
  deleteShInvoiceLineItem(shInvoiceLineItem) {
    this.set('state.isLoadingInvoice', true);

    this.get('shInvoiceLineItemsCommandService').deleteInvoiceLineItem(
      shInvoiceLineItem
    ).then(() => {
      this.get('applicationStateManager').addToast('Line Item Deleted');
      this.set('state.isLoadingInvoice', false);
      this.loadShInvoice(this.get('state.shInvoice.id'));
    }).catch(() => {
      // gobble gobble
      this.set('state.isLoadingInvoice', false);
    });
  },

  /**
   * Update state after fees have been updated
   *
   * @param bool|null madeFMS
   */
  feesUpdated(madeFMS = null) {
    this.applicationStateManager.addToast('Fees updated');

    this.loadShInvoice(this.get('state.shInvoice.id'));
  },

  /**
   * User has successfully added a line item, update state
   */
  lineItemAdded() {
    this.loadShInvoice(this.get('state.shInvoice.id'));
  },

  /**
   * User has successfully updated a line item, update state
   */
  lineItemEdited() {
    this.loadShInvoice(this.get('state.shInvoice.id'));
  },

  /**
   * Load the invoice for the view and set the state.
   *
   * @param {String} invoiceId
   * @return {*}
   */
  loadShInvoice(invoiceId) {
    this.set('state.isLoadingInvoice', true);

    return this.get('shInvoiceCommandService').loadShInvoice(
      invoiceId,
      PAYIN__SH_INVOICE_INCLUDES
    ).then(shInvoice => {
      this.set('state.shInvoice', shInvoice);
    }).catch(() => {
      // gobble gobble
    }).finally(() => this.set('state.isLoadingInvoice', false));
  },

  /**
   * Process the given ShInvoice
   */
  processShInvoice: task(function *() {
    yield this.get('shInvoiceCommandService')
      .processShInvoice(this.get('state.shInvoice'))
      .catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'Error processing invoice.  Please refresh and try again.',
          'processShInvoice -- error processing invoice id : ' + this.get('shInvoice.id'),
        );
        return;
      });

    yield this.loadShInvoice(this.get('state.shInvoice.id'));
  }),

  /**
   * Send an invoice to the user of the invoice.
   *
   * @return {Ember.RSVP.Promise}
   */
  sendInvoice() {
    this.set('state.isSendingInvoice', true);

    return this.get('shInvoiceCommandService').sendInvoice(
      this.get('state.shInvoice')
    ).then(() => {
      let modalModel = {
        titleText: 'Invoice sent',
        bodyText: 'You have successfully sent the invoice',
        actionButtonText: 'OK',
        isShowCancelButton: false,
      };

      showModal(this, 'confirmation', modalModel);
      this.get('state.shInvoice').set('lastSentAt', new Date());
    }).catch(() => {
      // gobble gobble
    }).finally(() => this.set('state.isSendingInvoice', false));
  },

  /**
   * @param {ShInvoiceTransaction} shInvoiceTransaction
   * @return {Promise<*>}
   */
  deleteTransaction(shInvoiceTransaction) {
    this.set('state.isLoadingInvoice', true);

    return this.get('shInvoiceTransactionCommandService').deleteTransaction(shInvoiceTransaction).then(() => {
      return this.get('shInvoiceCommandService').loadShInvoice(this.get('state.shInvoice.id'), PAYIN__SH_INVOICE_INCLUDES);
    }).then(() => {
      shInvoiceTransaction.unloadRecord();
    }).finally(() => {
      this.set('state.isLoadingInvoice', false);
    });
  },

  /**
   * Toggle whether we are editing the memo or not
   *
   * @param {Boolean} isEditing
   */
  toggleIsEditingMemo(isEditing) {
    this.set('state.updatedMemoText', this.get('state.shInvoice.memo'));
    this.set('state.isEditingMemo', isEditing);
  },

  /**
   * UnBatch the current invoice.
   *
   * @return {Ember.RSVP.Promise}
   */
  unBatchInvoice() {
    this.set('state.isUnBatchSending', true);

    return this.get('shInvoiceCommandService').unBatchInvoice(
      this.get('state.shInvoice')
    ).catch(() => {
      // gobble gobble
    }).finally(() => this.set('state.isUnBatchSending', false));
  },

  /**
   * Update the memo with what is in our state with the db.
   *
   * @return {Ember.RSVP.Promise}
   */
  updateInvoiceMemo() {
    this.set('state.isSavingMemo', true);

    return this.get('shInvoiceCommandService').addMemoToInvoice(
      this.get('state.shInvoice'),
      this.get('state.updatedMemoText'),
      PAYIN__SH_INVOICE_INCLUDES
    ).then(() => {
      this.set('state.updatedMemoText', '');
      this.set('state.isEditingMemo', false);
    }).finally(() => this.set('state.isSavingMemo', false));
  },

  updateInvoiceMemoInput(text) {
    this.set('state.updatedMemoText', text);
  },
});
